import jsPDF from "jspdf";
import html2canvas from "html2canvas";
/**
 * Handles the conversion of a specific DOM element (printRef.current) into a multi-page PDF.
 * It replaces form elements with static content and respects elements with the class ".pdf-break"
 * for manual page breaks.
 * @param {object} printRef - A React ref object pointing to the container DOM element.
 * @param {string} [name="Employee"] - The base name for the output PDF file.
 */
export const handlePrintToPDF = async (printRef, name = "Employee") => {
  const element = printRef.current;
  if (!element) return;

  // Store elements we'll temporarily hide or replace
  const noPrintEls = element.querySelectorAll(".no-print");
  const formEls = element.querySelectorAll("input, textarea, select");
  const replacements = [];

  try {
    // --- Phase 1: Prepare the DOM for html2canvas ---

    // 1. Hide control elements (e.g., Print buttons)
    noPrintEls.forEach((el) => {
      el.dataset._prevDisplay = el.style.display || "";
      el.style.display = "none";
    });

    // 2. Replace Form Elements with static DIVs for accurate rendering
    for (const el of formEls) {
      const style = window.getComputedStyle(el);
      const isCheck = el.type === "checkbox" || el.type === "radio";
      const div = document.createElement("div");

      // **Critical for Layout Fidelity:** Copy key box model and layout properties
      Object.assign(div.style, {
        width: style.width,
        height: style.height,
        minHeight: style.minHeight || "20px", // Ensure a minimal height
        margin: style.margin,
        padding: style.padding,
        border: style.border || "1px solid #000",
        borderRadius: style.borderRadius || "6px",
        boxSizing: style.boxSizing,
        display: style.display,
        position: style.position,
        zIndex: style.zIndex,
        float: style.float,
        // Text/Content styles
        background: "#fff", 
        color: "#000",
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        lineHeight: style.lineHeight,
        textAlign: style.textAlign,
        wordBreak: "break-word",
        whiteSpace: "normal",
      });

      if (isCheck) {
        // Special styling for checkboxes/radios
        const rect = el.getBoundingClientRect();
        const size = Math.min(rect.height || 20, rect.width || 20);

        Object.assign(div.style, {
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          border: "2.5px solid #000",
          borderRadius: el.type === "radio" ? "50%" : "4px",
          background: el.checked ? "#000" : "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          fontSize: `${size * 0.7}px`,
          fontWeight: "900",
          color: "#fff",
          lineHeight: "1",
        });

        div.textContent = el.checked
          ? el.type === "checkbox" ? "✓" : "●"
          : "";
      } else {
        // Text/Select elements content
        const text = el.tagName === "SELECT"
          ? el.options[el.selectedIndex]?.text || ""
          : el.value;
        div.textContent = text || " ";
      }

      // Hide the original and insert the replacement
      el.dataset._prevVisibility = el.style.visibility || "";
      el.style.visibility = "hidden";
      el.parentNode.insertBefore(div, el.nextSibling);
      replacements.push({ el, div });
    }

    // Give layout a tick to settle after DOM manipulation
    await new Promise((res) => setTimeout(res, 50));

    // 3. Find Manual Break Positions (.pdf-break)
    const pageBreakEls = element.querySelectorAll(".pdf-break");
    const boundaries = [0]; // Start at the top (0)
    const containerTop = element.getBoundingClientRect().top;

    pageBreakEls.forEach((el) => {
      // Use getBoundingClientRect for the most reliable position relative to the viewport
      const relativeTop = el.getBoundingClientRect().top - containerTop;
      // Push the pixel value where the break should occur
      boundaries.push(relativeTop); 
    });

    boundaries.push(element.scrollHeight); // Add end of content
    const uniqueBoundaries = Array.from(new Set(boundaries)).sort((a, b) => a - b);
    
    // 4. Capture Full Element Canvas Once
    const canvas = await html2canvas(element, {
      scale: 2, // High resolution for clarity
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      width: element.scrollWidth,
      height: element.scrollHeight,
      logging: false,
    });

    const canvasWidth = canvas.width;

    // --- Phase 2: Configure and Slice for PDF ---

    // 5. Setup PDF (A4, with Margins)
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidthMm = pdf.internal.pageSize.getWidth();
    const pageHeightMm = pdf.internal.pageSize.getHeight();

    const marginX = 10;
    const marginY = 10;
    const contentWidthMm = pageWidthMm - marginX * 2;
    const contentHeightMm = pageHeightMm - marginY * 2;

    const mmPerPx = 25.4 / 96; // Standard mm per DOM pixel (96 DPI)

    // Calculate final scaling factor based on the DOM width (canvasWidth / 2)
    const canvasWidthPx = canvasWidth / 2;
    const scale = contentWidthMm / (canvasWidthPx * mmPerPx);

    // Max height in DOM pixels that fits on one PDF page
    const pageHeightPx = contentHeightMm / (mmPerPx * scale);

    // 6. Slice by Manual Boundaries and Automatic Page Fills
    let isFirstPage = true;

    for (let i = 0; i < uniqueBoundaries.length - 1; i++) {
      let sectionStartPx = uniqueBoundaries[i]; // Start of current section (DOM pixels)
      const sectionEndPx = uniqueBoundaries[i + 1];

      // Loop to slice the current section into page-sized chunks
      while (sectionStartPx < sectionEndPx) {
        const remainingSectionHeightPx = sectionEndPx - sectionStartPx;
        
        // Height of the slice in unscaled DOM pixels
        const sliceHeightUnscaled = Math.min(
          remainingSectionHeightPx, 
          pageHeightPx
        );

        if (!isFirstPage) {
          pdf.addPage();
        }
        isFirstPage = false;

        // Calculate source and destination dimensions
        const sourceY = sectionStartPx * 2; // Y in canvas pixels (scale 2x)
        const sliceHeightCanvas = sliceHeightUnscaled * 2; // Height in canvas pixels (scale 2x)
        const sliceHeightMm = sliceHeightUnscaled * mmPerPx * scale; // Final height in mm

        // Create a canvas for this slice
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sliceHeightCanvas;

        const ctx = pageCanvas.getContext("2d");

        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvasWidth,
          sliceHeightCanvas, // Source (from big canvas)
          0,
          0,
          canvasWidth,
          sliceHeightCanvas // Destination
        );

        const imgData = pageCanvas.toDataURL("image/jpeg", 0.98);

        pdf.addImage(
          imgData,
          "JPEG",
          marginX,
          marginY,
          contentWidthMm,
          sliceHeightMm
        );

        // Advance to the next starting point for the next potential slice
        sectionStartPx += sliceHeightUnscaled;
      }
    }

    // --- Phase 3: Save and Cleanup ---

    // 7. Save PDF
    const fileName = `${(name || "Employee")
      .replace(/\s+/g, "_")
      .trim()}_${Date.now()}.pdf`;
    pdf.save(fileName);
  } catch (err) {
    console.error("PDF generation failed:", err);
    alert("PDF generation failed");
  } finally {
    // 8. RESTORE DOM STATE
    // Remove temporary divs and restore hidden elements
    replacements.forEach(({ el, div }) => {
      if (div && div.parentNode) div.parentNode.removeChild(div);
      el.style.visibility = el.dataset._prevVisibility || "";
      delete el.dataset._prevVisibility;
    });

    noPrintEls.forEach((el) => {
      el.style.display = el.dataset._prevDisplay || "";
      delete el.dataset._prevDisplay;
    });
  }
};




export const addTempPaddingToInputs = async (printCallback) => {

  const inputs = document.querySelectorAll("input, textarea, select");

  // Store original padding
  inputs.forEach((el) => {
    el.dataset.prevPaddingTop = el.style.paddingTop || "";
    el.dataset.prevPaddingBottom = el.style.paddingBottom || "";

    // Add padding Y-3 equivalent (Tailwind py-3 = 0.75rem top + bottom)
    el.style.paddingTop = "0.75rem";
    el.style.paddingBottom = "0.75rem";
  });

  try {
    // Run pdf generator
    await printCallback();

  } finally {
    // Restore original padding
    inputs.forEach((el) => {
      el.style.paddingTop = el.dataset.prevPaddingTop;
      el.style.paddingBottom = el.dataset.prevPaddingBottom;

      delete el.dataset.prevPaddingTop;
      delete el.dataset.prevPaddingBottom;
    });
  }
};